// Admin Portal — clone a client and its facilities.
//
// The screen is built around preview-then-apply because the endpoint is: a dry
// run takes exactly the same code path as the real one and rolls back instead
// of committing. So the report shown here is not an estimate — it is what will
// happen, produced by the code that will do it.
//
// Nothing is written until "Create clone" is pressed, and that button does not
// exist until a preview has succeeded.

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Copy, Loader2, X } from "lucide-react";
import { authFetch, facilitiesApi } from "../../utils/api";

// Must match FACILITY_ID_RE in client_clone.py. Checked here as well so a
// typo costs a keystroke rather than a round trip.
const FACILITY_ID_RE = /^[A-Z0-9_]{2,20}$/;

const suggestId = (sourceId, taken) => {
  const base = (sourceId || "FAC").replace(/[^A-Z0-9_]/gi, "").toUpperCase();
  for (let n = 2; n < 99; n += 1) {
    const candidate = `${base}_${n}`.slice(0, 20);
    if (!taken.has(candidate)) return candidate;
  }
  return "";
};

export const ClientCloneModal = ({ client, onClose, onCloned }) => {
  const [facilities, setFacilities] = useState(null);
  const [newName, setNewName] = useState(
    `${client.name || client.client_name || "Client"} (copy)`,
  );
  const [map, setMap] = useState({});
  const [includeUsers, setIncludeUsers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    facilitiesApi
      .getAll()
      .then((all) => {
        const mine = (Array.isArray(all) ? all : []).filter(
          (f) => f.client_id === client.id,
        );
        setFacilities(mine);
        const taken = new Set(
          (Array.isArray(all) ? all : []).map((f) => f.facility_id),
        );
        const seeded = {};
        mine.forEach((f) => {
          const s = suggestId(f.facility_id, taken);
          taken.add(s);
          seeded[f.facility_id] = s;
        });
        setMap(seeded);
      })
      .catch(() => setFacilities([]));
  }, [client.id]);

  // Any edit invalidates the preview: a report describing different target ids
  // than the ones in the boxes is worse than no report.
  const setTarget = (src, value) => {
    setMap((m) => ({ ...m, [src]: value.toUpperCase() }));
    setPreview(null);
  };

  const invalid = useMemo(() => {
    const vals = Object.values(map);
    const bad = vals.filter((v) => !FACILITY_ID_RE.test(v || ""));
    const dupes = vals.filter((v, i) => v && vals.indexOf(v) !== i);
    if (!newName.trim()) return "Give the new client a name.";
    if (!facilities?.length) return "This client has no facilities to clone.";
    if (bad.length)
      return "Facility ids: 2–20 characters, A–Z, 0–9 and underscore.";
    if (dupes.length) return "Target facility ids must be distinct.";
    return "";
  }, [map, newName, facilities]);

  const run = async (dryRun) => {
    setBusy(true);
    setError("");
    try {
      const res = await authFetch(`/admin/clients/${client.id}/clone`, {
        method: "POST",
        body: JSON.stringify({
          new_client_name: newName.trim(),
          facility_map: map,
          include_users: includeUsers,
          dry_run: dryRun,
        }),
      });
      if (res?.error) {
        setError(res.error);
        setPreview(null);
        return;
      }
      if (dryRun) {
        setPreview(res);
      } else {
        onCloned?.(res);
        onClose();
      }
    } catch {
      setError("Network error — nothing was created.");
    }
    setBusy(false);
  };

  const rows = preview
    ? Object.entries(preview.tables || {})
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Copy className="w-4 h-4" /> Clone{" "}
            {client.name || client.client_name}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Copies configuration only — facilities, services, tabs, prompts,
            branding, topics and survey definitions. Knowledge base content,
            conversations, field data and credentials are not copied.
          </p>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              New client name
            </label>
            <input
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setPreview(null);
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              New facility ids
            </label>
            {facilities === null ? (
              <div className="text-sm text-slate-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading facilities…
              </div>
            ) : facilities.length === 0 ? (
              <p className="text-sm text-slate-500">
                This client has no facilities.
              </p>
            ) : (
              <div className="space-y-2">
                {facilities.map((f) => (
                  <div key={f.facility_id} className="flex items-center gap-2">
                    <span className="text-sm text-slate-500 w-40 truncate">
                      {f.facility_id}
                      <span className="text-slate-400"> →</span>
                    </span>
                    <input
                      value={map[f.facility_id] || ""}
                      onChange={(e) => setTarget(f.facility_id, e.target.value)}
                      maxLength={20}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeUsers}
              onChange={(e) => {
                setIncludeUsers(e.target.checked);
                setPreview(null);
              }}
              className="mt-0.5 rounded border-slate-300"
            />
            <span>
              Give the same people access to the new client
              <span className="block text-xs text-slate-500">
                Links existing accounts. No account is duplicated — an account
                is keyed by email.
              </span>
            </span>
          </label>

          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Each new facility gets its own empty Pinecone namespace. The
              cloned knowledge base is empty and the facilities answer nothing
              until it is populated — vectors are not copied.
            </span>
          </div>

          {error && (
            <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {preview && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Preview — nothing has been written
              </div>
              <div className="p-3 space-y-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {rows.map(([t, n]) => (
                    <div key={t} className="flex justify-between">
                      <span className="text-slate-600 truncate">{t}</span>
                      <span className="text-slate-900 font-medium">{n}</span>
                    </div>
                  ))}
                </div>
                {Object.keys(preview.skipped || {}).length > 0 && (
                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer">
                      {Object.keys(preview.skipped).length} tables skipped
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                      {Object.entries(preview.skipped).map(([t, why]) => (
                        <li key={t}>
                          <span className="text-slate-600">{t}</span> — {why}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {(preview.notes || []).map((n, i) => (
                  <p key={i} className="text-xs text-slate-500">
                    {n}
                  </p>
                ))}
              </div>
            </div>
          )}

          {invalid && !error && (
            <p className="text-xs text-amber-700">{invalid}</p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-3 border-t border-slate-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => run(true)}
            disabled={busy || Boolean(invalid)}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && !preview && <Loader2 className="w-4 h-4 animate-spin" />}
            {preview ? "Re-check" : "Preview"}
          </button>
          {/* Only reachable once a preview has succeeded — the report shown is
              produced by the same code path that does the write. */}
          <button
            onClick={() => run(false)}
            disabled={busy || Boolean(invalid) || !preview}
            title={!preview ? "Preview first" : ""}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && preview ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            Create clone
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientCloneModal;
